import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Phone, User, Plus, Settings, Ban, Pencil, Trash2, ChevronDown, FolderOpen, Download } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import ProjektZeitstrahl from '../components/Kalender/ProjektZeitstrahl';
import Modal from '../components/Common/Modal';
import TerminForm from '../components/Termin/TerminForm';
import TerminZeiterfassung from '../components/Zeiterfassung/TerminZeiterfassung';
import { getKalenderEvents, importIcs } from '../api/kalenderApi';
import { createTermin, updateTermin, deleteTermin, getTerminById } from '../api/termineApi';
import { getAllBenutzer } from '../api/benutzerApi';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useTermineRefresh } from '../hooks/useDataRefresh';
import { useDistance } from '../hooks/useDistance';
import { useFirmendaten } from '../hooks/useFirmendaten';

function Kalender() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const calendarRef = useRef(null);
  const urlParamsProcessedRef = useRef(false);
  const { user: currentUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [geschaeftszeiten, setGeschaeftszeiten] = useState([]);
  const [loading, setLoading] = useState(false);
  // Bearbeiter-Filter: 'alle' | 'me' | 'unassigned' | '<benutzerId>'
  const [bearbeiterFilter, setBearbeiterFilter] = useState(() => {
    try { return localStorage.getItem('kalender.bearbeiterFilter') || 'alle'; } catch { return 'alle'; }
  });
  const handleBearbeiterFilterChange = (value) => {
    setBearbeiterFilter(value);
    try { localStorage.setItem('kalender.bearbeiterFilter', value); } catch {}
  };
  const [benutzerList, setBenutzerList] = useState([]);
  useEffect(() => {
    getAllBenutzer({ aktiv: 'true' })
      .then(data => setBenutzerList(data || []))
      .catch(err => console.error('Mitarbeiter-Liste konnte nicht geladen werden:', err));
  }, []);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingTermin, setEditingTermin] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentDateRange, setCurrentDateRange] = useState({ start: null, end: null });
  const [preselectedKunde, setPreselectedKunde] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProjektZeitstrahl, setShowProjektZeitstrahl] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const loadingDateRangeRef = useRef(null);

  // Firmendaten für Entfernungsberechnung
  const { firmendaten } = useFirmendaten();

  // Entfernung zum Termin-Ort berechnen
  const { formattedDistance, formattedDriveTime, loading: distanceLoading } = useDistance(
    selectedEvent?.ort,
    firmendaten
  );

  // Google Maps Popup State
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [mapPosition, setMapPosition] = useState({ top: 0, left: 0 });
  const mapPreviewTimeout = useRef(null);
  const addressRef = useRef(null);

  const handleAddressMouseEnter = (e) => {
    if (mapPreviewTimeout.current) {
      clearTimeout(mapPreviewTimeout.current);
    }
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    mapPreviewTimeout.current = setTimeout(() => {
      const popupWidth = 320;
      const popupHeight = 280;

      let left = mouseX + 15;
      let top = mouseY + 15;

      if (left + popupWidth > window.innerWidth - 10) {
        left = mouseX - popupWidth - 15;
      }
      if (top + popupHeight > window.innerHeight - 10) {
        top = mouseY - popupHeight - 15;
      }

      setMapPosition({ top, left });
      setShowMapPreview(true);
    }, 400);
  };

  const handleAddressMouseLeave = () => {
    if (mapPreviewTimeout.current) {
      clearTimeout(mapPreviewTimeout.current);
    }
    mapPreviewTimeout.current = setTimeout(() => {
      setShowMapPreview(false);
    }, 300);
  };

  const handleMapMouseEnter = () => {
    if (mapPreviewTimeout.current) {
      clearTimeout(mapPreviewTimeout.current);
    }
  };

  const handleMapMouseLeave = () => {
    setShowMapPreview(false);
  };

  // URL-Parameter verarbeiten (nur einmal nach dem initialen Laden)
  useEffect(() => {
    // Bereits verarbeitet? Nicht nochmal ausführen
    if (urlParamsProcessedRef.current) {
      return;
    }

    // Noch am Laden? Warten
    if (loading) {
      return;
    }

    // Prüfe ob Parameter vorhanden sind
    const terminId = searchParams.get('terminId');
    const neuerTermin = searchParams.get('neuerTermin');
    const kundeId = searchParams.get('kundeId');
    const kundeName = searchParams.get('kundeName');
    const kundeEmail = searchParams.get('kundeEmail');
    const kundeAdresse = searchParams.get('kundeAdresse');

    // Keine Parameter vorhanden - trotzdem als verarbeitet markieren
    if (!terminId && !neuerTermin) {
      urlParamsProcessedRef.current = true;
      return;
    }

    // Als verarbeitet markieren (sofort, um Re-Renders zu vermeiden)
    urlParamsProcessedRef.current = true;

    const processUrlParams = async () => {
      // Wenn ein Termin geöffnet werden soll
      if (terminId) {
        try {
          const termin = await getTerminById(terminId);
          if (termin) {
            // Zum Datum des Termins navigieren
            if (calendarRef.current) {
              const calendarApi = calendarRef.current.getApi();
              calendarApi.gotoDate(termin.datum);
            }

            // Termin-Details anzeigen
            setSelectedEvent({
              id: termin.id,
              type: 'termin',
              title: termin.kunde ? termin.kunde.name : termin.titel,
              start: new Date(termin.datum),
              end: new Date(termin.datum),
              termin: termin,
              kundeId: termin.kundeId,
              kundeName: termin.kunde?.name,
              titel: termin.titel,
              telefon: termin.kunde?.telefon,
              ort: termin.ort,
              beschreibung: termin.beschreibung,
              bearbeiter: termin.bearbeiter || null,
              isPrivat: !termin.kundeId
            });
            setIsDetailModalOpen(true);
          }
        } catch (error) {
          console.error('Error loading termin:', error);
        }
        // URL-Cleaning entfernt - verursachte Race Condition
        return;
      }

      // Wenn ein neuer Termin mit vorausgewähltem Kunden erstellt werden soll
      if (neuerTermin === 'true' && kundeId) {
        const kunde = {
          id: kundeId,
          name: kundeName ? decodeURIComponent(kundeName) : '',
          email: kundeEmail ? decodeURIComponent(kundeEmail) : ''
        };
        setPreselectedKunde(kunde);
        setEditingTermin({
          kundeId: kundeId,
          kunde: kunde,
          ort: kundeAdresse ? decodeURIComponent(kundeAdresse) : ''
        });
        setIsFormModalOpen(true);
      }
    };

    processUrlParams();
  }, [loading, searchParams]);

  const loadEvents = async (start, end) => {
    // Verhindere doppeltes Laden für den GLEICHEN Zeitraum
    const rangeKey = `${start}-${end}`;

    // Nur blockieren wenn EXAKT der gleiche Range
    if (loadingDateRangeRef.current === rangeKey) {
      console.log('⏭️ Skipping duplicate load for same range:', rangeKey);
      return;
    }

    console.log('🔄 Loading events for range:', rangeKey);
    loadingDateRangeRef.current = rangeKey;

    try {
      setLoading(true);
      const data = await getKalenderEvents(start, end);
      setEvents(data.events);
      setGeschaeftszeiten(data.geschaeftszeiten);
      console.log('✅ Events loaded:', data.events?.length || 0, 'events');
    } catch (error) {
      console.error('❌ Fehler beim Laden der Events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Echtzeit-Updates: Reagiere auf KI-Assistent Aktionen
  const handleTerminRefresh = useCallback(() => {
    if (currentDateRange.start && currentDateRange.end) {
      // Reset loading ref um Neuladen zu ermöglichen
      loadingDateRangeRef.current = null;
      loadEvents(currentDateRange.start, currentDateRange.end);
    }
  }, [currentDateRange]);

  useTermineRefresh(handleTerminRefresh);

  const handleDatesSet = (dateInfo) => {
    console.log('📅 Calendar Range Changed:', dateInfo.start, 'to', dateInfo.end);

    // WICHTIG: Range um ±1 Monat erweitern für Buffer
    const rangeStart = new Date(dateInfo.start);
    rangeStart.setMonth(rangeStart.getMonth() - 1); // 1 Monat zurück

    const rangeEnd = new Date(dateInfo.end);
    rangeEnd.setMonth(rangeEnd.getMonth() + 1); // 1 Monat voraus

    const startISO = rangeStart.toISOString();
    const endISO = rangeEnd.toISOString();

    console.log('📥 Loading events from:', startISO, 'to', endISO);

    // Update currentDateRange mit dem ERWEITERTEN Range
    setCurrentDateRange({ start: startISO, end: endISO });

    // Lade Events für erweiterten Range
    loadEvents(startISO, endISO);
  };

  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const extendedProps = event.extendedProps;

    if (extendedProps.type === 'termin') {
      setSelectedEvent({
        id: event.id,
        type: 'termin',
        title: event.title,
        start: event.start,
        end: event.end,
        ...extendedProps
      });
      setIsDetailModalOpen(true);
    } else if (extendedProps.type === 'blockiert') {
      setSelectedEvent({
        type: 'blockiert',
        title: event.title,
        start: event.start,
        end: event.end,
        ...extendedProps
      });
      setIsDetailModalOpen(true);
    }
  };

  const handleDateClick = (dateInfo) => {
    // Neuen Termin an diesem Datum erstellen
    setEditingTermin({
      datum: dateInfo.dateStr,
      uhrzeit: dateInfo.allDay ? '09:00' : dateInfo.date.toTimeString().slice(0, 5)
    });
    setIsFormModalOpen(true);
  };

  const formatKonfliktMessage = (konflikte) => {
    const lines = konflikte.map(k => {
      const datum = new Date(k.datum).toLocaleDateString('de-DE');
      const ressourcen = [
        ...k.fahrzeuge.map(f => `Fahrzeug ${f.kennzeichen}`),
        ...k.werkzeuge.map(w => `Werkzeug "${w.bezeichnung}"`)
      ].join(', ');
      return `• ${datum} ${k.uhrzeit} (${k.dauerMinuten}min): ${ressourcen}`;
    });
    return `Ressourcen-Konflikt:\n${lines.join('\n')}\n\nBitte Zeitpunkt anpassen oder andere Ressourcen wählen.`;
  };

  const extractErrorAlert = (error, fallback) => {
    if (error?.response?.status === 409 && Array.isArray(error.response?.data?.konflikte)) {
      return formatKonfliktMessage(error.response.data.konflikte);
    }
    if (error?.response?.data?.error) return error.response.data.error;
    return fallback;
  };

  const handleCreateTermin = async (terminData) => {
    try {
      setIsSaving(true);
      await createTermin(terminData);
      setIsFormModalOpen(false);
      setEditingTermin(null);
      // Events neu laden - Reset des Range-Refs damit neu geladen werden kann
      if (currentDateRange.start && currentDateRange.end) {
        loadingDateRangeRef.current = null;
        await loadEvents(currentDateRange.start, currentDateRange.end);
      }
    } catch (error) {
      console.error('Error creating termin:', error);
      alert(extractErrorAlert(error, 'Fehler beim Erstellen des Termins'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateTermin = async (terminData) => {
    try {
      setIsSaving(true);
      await updateTermin(editingTermin.id, terminData);
      setIsFormModalOpen(false);
      setEditingTermin(null);
      setIsDetailModalOpen(false);
      setSelectedEvent(null);
      // Events neu laden - Reset des Range-Refs damit neu geladen werden kann
      if (currentDateRange.start && currentDateRange.end) {
        loadingDateRangeRef.current = null;
        await loadEvents(currentDateRange.start, currentDateRange.end);
      }
    } catch (error) {
      console.error('Error updating termin:', error);
      alert(extractErrorAlert(error, 'Fehler beim Aktualisieren des Termins'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTermin = async () => {
    if (!selectedEvent || !confirm('Möchten Sie diesen Termin wirklich löschen?')) {
      return;
    }

    try {
      await deleteTermin(selectedEvent.id);
      setIsDetailModalOpen(false);
      setSelectedEvent(null);
      // Events neu laden - Reset des Range-Refs damit neu geladen werden kann
      if (currentDateRange.start && currentDateRange.end) {
        loadingDateRangeRef.current = null;
        await loadEvents(currentDateRange.start, currentDateRange.end);
      }
    } catch (error) {
      console.error('Error deleting termin:', error);
      alert('Fehler beim Löschen des Termins');
    }
  };

  const handleIcsImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!window.confirm(`ICS-Datei "${file.name}" importieren?\n\nTermine werden als Privat-Termine erstellt. Bereits vorhandene Termine (gleiche Zeit + Titel) werden übersprungen.`)) {
      e.target.value = '';
      return;
    }

    try {
      setIsImporting(true);
      const result = await importIcs(file);
      alert(`Import abgeschlossen!\n\nImportiert: ${result.imported}\nÜbersprungen (Duplikate): ${result.skipped}${result.errors > 0 ? `\nFehler: ${result.errors}` : ''}`);
      if (currentDateRange.start && currentDateRange.end) {
        loadingDateRangeRef.current = null;
        await loadEvents(currentDateRange.start, currentDateRange.end);
      }
    } catch (error) {
      console.error('Error importing ICS:', error);
      alert(error.response?.data?.error || 'Fehler beim Importieren der ICS-Datei');
    } finally {
      setIsImporting(false);
      e.target.value = '';
    }
  };

  const openEditModal = () => {
    setEditingTermin(selectedEvent.termin);
    setIsDetailModalOpen(false);
    setIsFormModalOpen(true);
  };

  const formatTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Geschäftszeiten für FullCalendar businessHours formatieren
  const getBusinessHours = () => {
    return geschaeftszeiten
      .filter(gz => gz.aktiv)
      .map(gz => ({
        daysOfWeek: [gz.wochentag],
        startTime: gz.startzeit,
        endTime: gz.endzeit
      }));
  };

  return (
    <Layout>
      <style>{`
        .fc-event {
          cursor: default;
          transition: filter 0.15s ease, transform 0.15s ease;
        }
        .fc-event:hover {
          cursor: pointer;
          filter: brightness(1.15);
          transform: scale(1.02);
        }
        .fc-daygrid-day {
          cursor: default;
        }
        .fc-timegrid-slot {
          cursor: default;
        }

        /* Mobile FullCalendar Styles */
        @media (max-width: 767px) {
          .fc .fc-toolbar {
            flex-direction: column;
            gap: 8px;
          }
          .fc .fc-toolbar-chunk {
            display: flex;
            justify-content: center;
          }
          .fc .fc-toolbar-title {
            font-size: 1.1rem !important;
          }
          .fc .fc-button {
            padding: 6px 10px !important;
            font-size: 0.75rem !important;
          }
          .fc .fc-button-group .fc-button {
            padding: 6px 8px !important;
          }
          .fc .fc-daygrid-day-number {
            font-size: 0.8rem;
            padding: 4px !important;
          }
          .fc .fc-daygrid-event {
            font-size: 0.7rem !important;
            padding: 1px 3px !important;
          }
          .fc .fc-col-header-cell-cushion {
            font-size: 0.75rem;
            padding: 4px 2px !important;
          }
          .fc .fc-timegrid-slot-label {
            font-size: 0.7rem !important;
          }
          .fc .fc-timegrid-event {
            font-size: 0.7rem !important;
          }
          .fc .fc-list-event-title {
            font-size: 0.85rem;
          }
          .fc .fc-list-day-cushion {
            font-size: 0.8rem;
            padding: 6px 8px !important;
          }
        }
      `}</style>

      <div className={`container mx-auto ${isMobile ? 'px-4 py-4 pb-32' : 'px-6 py-8'}`}>
        {/* Header - Desktop */}
        {!isMobile && (
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-serif font-bold">Kalender</h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Bearbeiter-Filter */}
              <select
                value={bearbeiterFilter}
                onChange={(e) => handleBearbeiterFilterChange(e.target.value)}
                className={`px-3 py-2 rounded-button text-sm font-medium border transition focus:outline-none focus:ring-2 focus:ring-primary ${
                  bearbeiterFilter !== 'alle'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white border-border text-text hover:bg-gray-50'
                }`}
                title="Filter nach Mitarbeiter"
              >
                <option value="alle">Alle Termine</option>
                {currentUser?.id && <option value="me">Nur meine</option>}
                <option value="unassigned">Nicht zugewiesen</option>
                {benutzerList.length > 0 && (
                  <optgroup label="Mitarbeiter">
                    {benutzerList.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <button
                onClick={() => {
                  setEditingTermin(null);
                  setIsFormModalOpen(true);
                }}
                className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition"
              >
                <Plus className="w-5 h-5" />
                Neuer Termin
              </button>
              <label className={`flex items-center gap-2 px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition cursor-pointer ${isImporting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <Download className="w-4 h-4" />
                {isImporting ? 'Importiert...' : 'ICS Import'}
                <input
                  type="file"
                  accept=".ics,text/calendar"
                  onChange={handleIcsImport}
                  disabled={isImporting}
                  className="hidden"
                />
              </label>
              <button
                onClick={() => navigate('/kalender/geschaeftszeiten')}
                className="flex items-center gap-2 px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition"
              >
                <Settings className="w-4 h-4" />
                Geschäftszeiten
              </button>
              <button
                onClick={() => navigate('/kalender/blockierte-zeiten')}
                className="flex items-center gap-2 px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition"
              >
                <Ban className="w-4 h-4" />
                Blockierte Zeiten
              </button>
              <button
                onClick={() => setShowProjektZeitstrahl(!showProjektZeitstrahl)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-button transition ${
                  showProjektZeitstrahl
                    ? 'bg-primary text-white border-primary'
                    : 'border-border hover:bg-gray-50'
                }`}
              >
                <FolderOpen className="w-4 h-4" />
                Projekt-Zeitstrahl
              </button>
            </div>
          </div>
        )}

        {/* Header - Mobile */}
        {isMobile && (
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold">Kalender</h1>
              <div className="relative">
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded-lg"
                >
                  <Settings className="w-4 h-4" />
                  <ChevronDown className={`w-4 h-4 transition-transform ${showMobileMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Mobile Dropdown Menu */}
                {showMobileMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowMobileMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[180px]">
                      <button
                        onClick={() => {
                          navigate('/kalender/geschaeftszeiten');
                          setShowMobileMenu(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm hover:bg-gray-50"
                      >
                        <Settings className="w-4 h-4 text-gray-500" />
                        Geschäftszeiten
                      </button>
                      <button
                        onClick={() => {
                          navigate('/kalender/blockierte-zeiten');
                          setShowMobileMenu(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm hover:bg-gray-50 border-t border-gray-100"
                      >
                        <Ban className="w-4 h-4 text-gray-500" />
                        Blockierte Zeiten
                      </button>
                      <button
                        onClick={() => {
                          setShowProjektZeitstrahl(!showProjektZeitstrahl);
                          setShowMobileMenu(false);
                        }}
                        className={`flex items-center gap-2 w-full px-4 py-3 text-left text-sm border-t border-gray-100 ${
                          showProjektZeitstrahl ? 'bg-primary-light text-primary' : 'hover:bg-gray-50'
                        }`}
                      >
                        <FolderOpen className={`w-4 h-4 ${showProjektZeitstrahl ? 'text-primary' : 'text-gray-500'}`} />
                        Projekt-Zeitstrahl
                      </button>
                      <label className={`flex items-center gap-2 w-full px-4 py-3 text-left text-sm border-t border-gray-100 cursor-pointer ${isImporting ? 'opacity-50' : 'hover:bg-gray-50'}`}>
                        <Download className="w-4 h-4 text-gray-500" />
                        {isImporting ? 'Importiert...' : 'ICS Import'}
                        <input
                          type="file"
                          accept=".ics,text/calendar"
                          onChange={(e) => { handleIcsImport(e); setShowMobileMenu(false); }}
                          disabled={isImporting}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Calendar */}
        <div className={`bg-surface rounded-card shadow-card ${isMobile ? 'p-2' : 'p-6'} relative`}>
          {/* Loading Overlay - Kalender bleibt immer gemountet! */}
          {loading && (
            <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-card">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}
          <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
              headerToolbar={isMobile ? {
                left: 'prev,next',
                center: 'title',
                right: 'dayGridMonth,listWeek'
              } : {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
              }}
              locale="de"
              firstDay={1}
              events={(() => {
                if (bearbeiterFilter === 'alle') return events;
                return events.filter(ev => {
                  // Nicht-Termin-Events (blockierteZeiten, geschaeftszeiten etc.) immer anzeigen
                  const type = ev.extendedProps?.type;
                  if (type && type !== 'termin') return true;
                  const b = ev.extendedProps?.bearbeiter;
                  const bId = b?.id || null;
                  if (bearbeiterFilter === 'me') return bId === currentUser?.id;
                  if (bearbeiterFilter === 'unassigned') return !bId;
                  return bId === bearbeiterFilter;
                });
              })()}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              datesSet={handleDatesSet}
              businessHours={getBusinessHours()}
              slotMinTime="06:00:00"
              slotMaxTime="20:00:00"
              allDaySlot={true}
              allDayText="Ganztags"
              nowIndicator={true}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              buttonText={{
                today: 'Heute',
                month: 'Monat',
                week: 'Woche',
                day: 'Tag',
                list: 'Liste'
              }}
              height="auto"
              eventDisplay="block"
              dayMaxEvents={isMobile ? 2 : 3}
              moreLinkText={(num) => `+${num}`}
            />
        </div>

        {/* Projekt-Zeitstrahl */}
        {showProjektZeitstrahl && (
          <div className={`${isMobile ? 'mt-4' : 'mt-6'}`}>
            <ProjektZeitstrahl />
          </div>
        )}

        {/* Mobile FAB - Neuer Termin (links vom Chat-Button positioniert) */}
        {isMobile && (
          <button
            onClick={() => {
              setEditingTermin(null);
              setIsFormModalOpen(true);
            }}
            className="fixed right-20 z-40 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-dark active:scale-95 transition-all"
            style={{
              bottom: 'calc(24px + var(--bottom-nav-height, 64px) + var(--safe-area-bottom, 0px) + 16px)'
            }}
            aria-label="Neuer Termin"
          >
            <Plus className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Termin-Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedEvent(null);
        }}
        title={selectedEvent?.type === 'termin' ? 'Termin Details' : 'Blockierte Zeit'}
      >
        {selectedEvent && selectedEvent.type === 'termin' && (
          <div className="space-y-4">
            {/* Kunde oder Privat */}
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-text-muted mt-0.5" />
              <div>
                {selectedEvent.isPrivat ? (
                  <div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full mr-2">Privat</span>
                    <span className="font-medium">{selectedEvent.titel || 'Privat-Termin'}</span>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium">{selectedEvent.kundeName}</p>
                    {selectedEvent.telefon && (
                      <a
                        href={`tel:${selectedEvent.telefon}`}
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <Phone className="w-3 h-3" />
                        {selectedEvent.telefon}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Datum & Zeit */}
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-text-muted mt-0.5" />
              <div>
                <p>{formatDate(selectedEvent.start)}</p>
                <p className="text-sm text-text-muted">
                  {formatTime(selectedEvent.start)} - {formatTime(selectedEvent.end)}
                </p>
              </div>
            </div>

            {/* Ort */}
            {selectedEvent.ort && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-text-muted mt-0.5" />
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    ref={addressRef}
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedEvent.ort)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary"
                    onMouseEnter={handleAddressMouseEnter}
                    onMouseLeave={handleAddressMouseLeave}
                  >
                    {selectedEvent.ort}
                  </a>
                  {formattedDistance && (
                    <span className="text-xs px-2 py-0.5 bg-primary-light text-primary rounded-full font-medium">
                      {formattedDistance} · {formattedDriveTime}
                    </span>
                  )}
                  {distanceLoading && (
                    <span className="inline-block w-3 h-3 border border-gray-300 border-t-primary rounded-full animate-spin"></span>
                  )}
                </div>
              </div>
            )}

            {/* Zugewiesener Mitarbeiter */}
            {selectedEvent.bearbeiter && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-text-muted mt-0.5" />
                <div className="flex items-center gap-2">
                  {selectedEvent.bearbeiter.farbe && (
                    <span
                      className="inline-block w-3 h-3 rounded-full border border-white shadow-sm"
                      style={{ backgroundColor: selectedEvent.bearbeiter.farbe }}
                    />
                  )}
                  <div>
                    <p className="text-xs text-text-muted">Zugewiesen an</p>
                    <p className="font-medium">
                      {selectedEvent.bearbeiter.name}
                      {selectedEvent.bearbeiter.loginAktiv === false && (
                        <span className="ml-2 text-xs text-text-muted font-normal">(ohne Login)</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Beschreibung */}
            {selectedEvent.beschreibung && (
              <div className="pt-3 border-t border-border">
                <p className="text-sm text-text-muted mb-1">Beschreibung</p>
                <p>{selectedEvent.beschreibung}</p>
              </div>
            )}

            {/* Aktionen */}
            <div className="flex gap-3 pt-4 border-t border-border">
              <button
                onClick={openEditModal}
                className="flex items-center justify-center gap-2 flex-1 bg-primary text-white py-2 rounded-button font-medium hover:bg-primary-dark transition"
              >
                <Pencil className="w-4 h-4" />
                Bearbeiten
              </button>
              <button
                onClick={handleDeleteTermin}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-danger rounded-button font-medium hover:bg-red-100 transition"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              {!selectedEvent.isPrivat && selectedEvent.kundeId && (
                <button
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    navigate(`/kunden/${selectedEvent.kundeId}`);
                  }}
                  className="flex-1 bg-gray-200 text-text py-2 rounded-button font-medium hover:bg-gray-300 transition"
                >
                  Zum Kunden
                </button>
              )}
            </div>

            {/* Zeiterfassung - nur für Kunden-Termine */}
            {!selectedEvent.isPrivat && selectedEvent.id && (
              <TerminZeiterfassung
                terminId={selectedEvent.id}
                kundeId={selectedEvent.kundeId}
              />
            )}
          </div>
        )}

        {selectedEvent && selectedEvent.type === 'blockiert' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Ban className="w-5 h-5 text-danger mt-0.5" />
              <div>
                <p className="font-medium">{selectedEvent.title}</p>
                {selectedEvent.grund && (
                  <p className="text-sm text-text-muted">{selectedEvent.grund}</p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-text-muted mt-0.5" />
              <div>
                <p>{formatDate(selectedEvent.start)}</p>
                {selectedEvent.end && selectedEvent.start?.toDateString() !== selectedEvent.end?.toDateString() && (
                  <p className="text-sm text-text-muted">bis {formatDate(selectedEvent.end)}</p>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-border">
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="flex-1 bg-gray-200 text-text py-2 rounded-button font-medium hover:bg-gray-300 transition"
              >
                Schließen
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Termin-Formular Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingTermin(null);
          setPreselectedKunde(null);
        }}
        title={editingTermin?.id ? 'Termin bearbeiten' : 'Neuer Termin'}
      >
        <TerminForm
          termin={editingTermin}
          preselectedKunde={preselectedKunde}
          onSubmit={editingTermin?.id ? handleUpdateTermin : handleCreateTermin}
          onCancel={() => {
            setIsFormModalOpen(false);
            setEditingTermin(null);
            setPreselectedKunde(null);
          }}
          isLoading={isSaving}
        />
      </Modal>

      {/* Google Maps Preview Popup */}
      {showMapPreview && selectedEvent?.ort && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: -5 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="fixed z-[60] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{
            top: mapPosition.top,
            left: mapPosition.left,
            width: '320px',
            maxWidth: 'calc(100vw - 16px)'
          }}
          onMouseEnter={handleMapMouseEnter}
          onMouseLeave={handleMapMouseLeave}
        >
          <div className="relative">
            <iframe
              title="Google Maps Preview"
              width="100%"
              height="200"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(selectedEvent.ort)}&output=embed&z=15`}
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/10 to-transparent" />
          </div>
          <div className="p-3 bg-white">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedEvent.ort}</p>
                <p className="text-xs text-gray-500 mt-0.5">Klicken für Vollbild in Google Maps</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </Layout>
  );
}

export default Kalender;
